// visualizer.js
import butterchurn from "butterchurn";
import butterchurnPresets from "butterchurn-presets";

/**
 * Initialize Butterchurn visualizer for a given <audio> + <canvas>.
 *
 * Layout rules:
 * - CSS controls the visualizer box (mobile vs desktop sizing).
 * - JS keeps the canvas's internal resolution in sync with that box.
 *
 * Audio rules:
 * - We *tap* the audio via Web Audio, but never control playback here.
 * - Enabling/disabling only starts/stops the render loop and (optionally) clears the canvas.
 */
export function initVisualizer(audioElem, canvasElem) {
  if (!audioElem || !canvasElem) {
    console.warn(
      "[Visualizer] Missing audioElem or canvasElem; aborting init."
    );
    return null;
  }

  // ---------------------------------
  // AudioContext + audio graph
  // ---------------------------------
  let audioCtx;
  try {
    const Ctor = window.AudioContext || window.webkitAudioContext;
    if (!Ctor) {
      console.warn("[Visualizer] Web Audio API not supported in this browser.");
      return null;
    }
    audioCtx = new Ctor();
  } catch (err) {
    console.error("[Visualizer] Failed to create AudioContext:", err);
    return null;
  }

  let sourceNode;
  try {
    sourceNode = audioCtx.createMediaElementSource(audioElem);
  } catch (err) {
    console.error(
      "[Visualizer] Failed to create MediaElementSource. " +
        "This usually means the audio element is already bound to another AudioContext.",
      err
    );
    return null;
  }

  const delayNode = audioCtx.createDelay();
  delayNode.delayTime.value = 0.75;

  const gainNode = audioCtx.createGain();
  gainNode.gain.value = 1.0;

  sourceNode.connect(delayNode);
  delayNode.connect(gainNode);
  gainNode.connect(audioCtx.destination);

  // ---------------------------------
  // Butterchurn visualizer setup
  // ---------------------------------
  const isMobile = window.innerWidth < 600;
  const dpr = window.devicePixelRatio || 1;
  const pixelRatio = isMobile ? Math.min(dpr, 1.5) : dpr;
  const textureRatio = isMobile ? 0.7 : 1;

  // Start with something, but we'll immediately sync to layout with updateRendererSize()
  const initialWidth = canvasElem.clientWidth || 800;
  const initialHeight = canvasElem.clientHeight || 600;

  let visualizer;
  try {
    visualizer = butterchurn.createVisualizer(audioCtx, canvasElem, {
      width: initialWidth,
      height: initialHeight,
      pixelRatio,
      textureRatio,
    });
  } catch (err) {
    console.error("[Visualizer] Failed to create Butterchurn visualizer:", err);
    return null;
  }

  try {
    visualizer.connectAudio(gainNode);
  } catch (err) {
    console.error("[Visualizer] Failed to connect audio to Butterchurn:", err);
  }

  // ---------------------------------
  // Renderer size sync with CSS layout
  // ---------------------------------
  /**
   * Keep the visualizer's internal resolution in sync with the canvas's
   * displayed size (which is controlled by CSS / media queries).
   */
  function updateRendererSize() {
    try {
      const rect = canvasElem.getBoundingClientRect();
      const width = Math.max(1, rect.width);
      const height = Math.max(1, rect.height);

      // Match the canvas resolution to its on-screen size * pixelRatio
      const renderWidth = Math.round(width * pixelRatio);
      const renderHeight = Math.round(height * pixelRatio);

      // Update the canvas backing store
      canvasElem.width = renderWidth;
      canvasElem.height = renderHeight;

      if (typeof visualizer.setRendererSize === "function") {
        visualizer.setRendererSize(renderWidth, renderHeight);
      }
    } catch (err) {
      console.warn("[Visualizer] Failed to update renderer size:", err);
    }
  }

  // Initial sync after creation
  updateRendererSize();

  // Keep up with window resize
  let resizeTimeout = null;
  function handleWindowResize() {
    if (resizeTimeout) window.clearTimeout(resizeTimeout);
    resizeTimeout = window.setTimeout(() => {
      updateRendererSize();
    }, 150);
  }
  window.addEventListener("resize", handleWindowResize);

  // If available, also watch the container for size changes (safer for layout tweaks).
  const vizContainer = document.getElementById("visualizerContainer");
  let resizeObserver = null;
  if (window.ResizeObserver && vizContainer) {
    resizeObserver = new ResizeObserver(() => {
      updateRendererSize();
    });
    resizeObserver.observe(vizContainer);
  }

  // ---------------------------------
  // Presets + auto-cycle
  // ---------------------------------
  // const presets = butterchurnPresets.getPresets();
  // const presetKeys = Object.keys(presets);

  // const goodMobilePresets = [
  //   "martin - another kind of groove",
  //   "Aderrasi - Songflower (Moss Posy)",
  //   "flexi + amandio c - organic [random mashup]",
  //   "Geiss - Cauldron - painterly 2 (saturation remix)",
  //   "$$$ Royal - Mashup (197)",
  // ].filter((name) => presetKeys.includes(name));

  // const usablePresets =
  //   isMobile && goodMobilePresets.length > 0 ? goodMobilePresets : presetKeys;

  // // Track which preset is currently active so the UI can show its name
  // let currentPresetKey = null;

  // function updatePresetLabel() {
  //   const el = document.getElementById("vizPresetName");
  //   if (!el) return;

  //   if (!enabled) {
  //     // Visualizer is off
  //     el.textContent = "Off";
  //   } else if (currentPresetKey) {
  //     // We know which preset is active
  //     el.textContent = currentPresetKey;
  //   } else {
  //     // Fallback if something hasn't set currentPresetKey yet
  //     el.textContent = "Default";
  //   }
  // }

  // function pickRandomPresetKey() {
  //   if (!usablePresets.length) return null;
  //   const idx = Math.floor(Math.random() * usablePresets.length);
  //   return usablePresets[idx];
  // }

  // const firstKey = pickRandomPresetKey();
  // if (firstKey && presets[firstKey]) {
  //   try {
  //     visualizer.loadPreset(presets[firstKey], 0.0);
  //     // 🔹 remember which preset we picked so the UI can show it

  //     currentPresetKey = firstKey;
  //     console.log("[Visualizer] Initial preset:", firstKey);

  //     // If you have updatePresetLabel() in this file, this is safe:
  //     // updatePresetLabel();
  //   } catch (err) {
  //     console.error("[Visualizer] Failed to load initial preset:", err);
  //   }
  // }
  // const AUTO_CYCLE_ENABLED = true;
  // const CYCLE_INTERVAL = isMobile ? 20000 : 15000;
  // const BLEND_TIME = 3.0;

  // let cycleTimer = null;

  // function cyclePreset() {
  //   if (!AUTO_CYCLE_ENABLED) return;
  //   if (!enabled) return; // don't bother cycling when viz is off

  //   const key = pickRandomPresetKey();
  //   if (!key || !presets[key]) return;
  //   try {
  //     currentPresetKey = key;
  //     visualizer.loadPreset(presets[key], BLEND_TIME);
  //     updatePresetLabel(); // live update the drawer label
  //   } catch (err) {
  //     console.error("[Visualizer] Failed to cycle preset:", err);
  //   }
  // }

  // if (AUTO_CYCLE_ENABLED && usablePresets.length > 1) {
  //   cycleTimer = window.setInterval(cyclePreset, CYCLE_INTERVAL);
  // }

    // ---------------------------------
  // Presets + auto-cycle
  // ---------------------------------
  const presets = butterchurnPresets.getPresets();
  const presetKeys = Object.keys(presets);

  const goodMobilePresets = [
    'martin - another kind of groove',
    'Aderrasi - Songflower (Moss Posy)',
    'flexi + amandio c - organic [random mashup]',
    'Geiss - Cauldron - painterly 2 (saturation remix)',
  ].filter((name) => presetKeys.includes(name));

  const usablePresets =
    isMobile && goodMobilePresets.length > 0 ? goodMobilePresets : presetKeys;

  // Track the currently active preset key so the UI can show it
  let currentPresetKey = null;

  // Update the drawer label: "Off" vs current preset name
  function updatePresetLabel() {
    const el = document.getElementById('vizPresetName');
    if (!el) return;

    if (!enabled) {
      // Visualizer is off
      el.textContent = 'Off';
    } else if (currentPresetKey) {
      // We know which preset is active
      el.textContent = currentPresetKey;
    } else {
      // Fallback if something hasn't set currentPresetKey yet
      el.textContent = 'Default';
    }
  }

  function pickRandomPresetKey() {
    if (!usablePresets.length) return null;
    const idx = Math.floor(Math.random() * usablePresets.length);
    return usablePresets[idx];
  }

 const firstKey = pickRandomPresetKey();
if (firstKey && presets[firstKey]) {
  try {
    visualizer.loadPreset(presets[firstKey], 0.0);

    // 🔹 tell the rest of the app what preset we picked
    if (typeof window !== 'undefined') {
      window.__SV_VIS_LAST_PRESET_NAME = firstKey;
    }

    console.log('[Visualizer] Initial preset:', firstKey);
  } catch (err) {
    console.error('[Visualizer] Failed to load initial preset:', err);
  }
}

  const AUTO_CYCLE_ENABLED = true;
  const CYCLE_INTERVAL = isMobile ? 20000 : 15000;
  const BLEND_TIME = 3.0;

  let cycleTimer = null;

function cyclePreset() {
  if (!AUTO_CYCLE_ENABLED) return;
  const key = pickRandomPresetKey();
  if (!key || !presets[key]) return;
  try {
    visualizer.loadPreset(presets[key], BLEND_TIME);

    // 🔹 keep the global in sync on every auto-cycle
    if (typeof window !== 'undefined') {
      window.__SV_VIS_LAST_PRESET_NAME = key;
    }
  } catch (err) {
    console.error('[Visualizer] Failed to cycle preset:', err);
  }
}
  if (AUTO_CYCLE_ENABLED && usablePresets.length > 1) {
    cycleTimer = window.setInterval(cyclePreset, CYCLE_INTERVAL);
  }


  // ---------------------------------
  // Render loop (controlled by setEnabled)
  // ---------------------------------
  let enabled = false;
  let animFrameId = null;

  function renderFrame() {
    if (!enabled) {
      animFrameId = null;
      return;
    }
    try {
      visualizer.render();
    } catch (err) {
      console.error("[Visualizer] Render error; disabling visualizer:", err);
      enabled = false;
      animFrameId = null;
      return;
    }
    animFrameId = window.requestAnimationFrame(renderFrame);
  }

  function ensureRenderLoop() {
    if (!enabled) return;
    if (audioElem.paused) return;
    if (animFrameId != null) return;
    animFrameId = window.requestAnimationFrame(renderFrame);
  }

  const onPlay = async () => {
    try {
      if (audioCtx.state === "suspended") {
        await audioCtx.resume();
      }
    } catch (err) {
      console.error("[Visualizer] Failed to resume AudioContext:", err);
    }
    ensureRenderLoop();
  };

  const onPause = () => {
    if (animFrameId != null) {
      window.cancelAnimationFrame(animFrameId);
      animFrameId = null;
    }
  };

  const onEnded = () => {
    if (animFrameId != null) {
      window.cancelAnimationFrame(animFrameId);
      animFrameId = null;
    }
  };

  audioElem.addEventListener("play", onPlay);
  audioElem.addEventListener("pause", onPause);
  audioElem.addEventListener("ended", onEnded);

  // ---------------------------------
  // Fullscreen (desktop-only) + auto-hide main UI
  // ---------------------------------
  const fullscreenBtn = document.getElementById("fullscreenBtn");
  const playerRoot = document.getElementById("audioPlayer");

  const hardwareThreads = navigator.hardwareConcurrency || 2;
  const isDesktop = window.innerWidth >= 768 && hardwareThreads >= 2;

  let hideUITimer = null;
  const HIDE_DELAY = 2000;

  function hideUI() {
    if (!document.fullscreenElement || !playerRoot) return;
    playerRoot.classList.remove("fullscreen-ui-visible");
    playerRoot.classList.add("fullscreen-ui-hidden");
  }

  function showUI() {
    if (!document.fullscreenElement || !playerRoot) return;
    playerRoot.classList.remove("fullscreen-ui-hidden");
    playerRoot.classList.add("fullscreen-ui-visible");

    if (hideUITimer) window.clearTimeout(hideUITimer);
    hideUITimer = window.setTimeout(hideUI, HIDE_DELAY);
  }

  if (fullscreenBtn && vizContainer && playerRoot) {
    if (!isDesktop) {
      fullscreenBtn.style.display = "none";
    } else {
      fullscreenBtn.addEventListener("click", async () => {
        try {
          if (!document.fullscreenElement) {
            await vizContainer.requestFullscreen();
          } else {
            await document.exitFullscreen();
          }
        } catch (err) {
          console.error("[Visualizer] Fullscreen error:", err);
        }
      });

      document.addEventListener("fullscreenchange", () => {
        const entering = document.fullscreenElement === vizContainer;

        if (entering) {
          console.log("[Visualizer] Entered fullscreen on visualizerContainer");

          // When we go fullscreen, the container size changes → resync renderer
          updateRendererSize();

          playerRoot.classList.remove("fullscreen-ui-hidden");
          playerRoot.classList.add("fullscreen-ui-visible");

          if (hideUITimer) window.clearTimeout(hideUITimer);
          hideUITimer = window.setTimeout(hideUI, HIDE_DELAY);
        } else {
          console.log("[Visualizer] Exited fullscreen");

          // Reset visibility
          playerRoot.classList.remove("fullscreen-ui-hidden");
          playerRoot.classList.add("fullscreen-ui-visible");

          if (hideUITimer) {
            window.clearTimeout(hideUITimer);
            hideUITimer = null;
          }

          // Container popped back to drawer size → resync again
          updateRendererSize();
        }
      });

      window.addEventListener("mousemove", showUI);
      window.addEventListener("touchstart", showUI, { passive: true });
    }
  }

    // ---------------------------------
  // Public controller API
  // ---------------------------------
  function setEnabled(nextEnabled) {
    const shouldEnable = Boolean(nextEnabled);
    if (shouldEnable === enabled) return;

    enabled = shouldEnable;

    // 🔹 Whenever ON/OFF changes, refresh the drawer label
    updatePresetLabel();

    if (enabled) {
      // Just visuals: audio graph is already wired
      ensureRenderLoop();
    } else {
      if (animFrameId != null) {
        window.cancelAnimationFrame(animFrameId);
        animFrameId = null;
      }
      // Optional: clear canvas when disabled
      try {
        const ctx = canvasElem.getContext('2d');
        if (ctx) {
          ctx.save();
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, canvasElem.width, canvasElem.height);
          ctx.fillStyle = 'black';
          ctx.fillRect(0, 0, canvasElem.width, canvasElem.height);
          ctx.restore();
        }
      } catch (err) {
        console.warn('[Visualizer] Failed to clear canvas on disable:', err);
      }
    }
  }

  function isEnabled() {
    return enabled;
  }

  function destroy() {
    enabled = false;

    if (animFrameId != null) {
      window.cancelAnimationFrame(animFrameId);
      animFrameId = null;
    }

    try {
      audioElem.removeEventListener("play", onPlay);
      audioElem.removeEventListener("pause", onPause);
      audioElem.removeEventListener("ended", onEnded);
    } catch {
      // non-fatal
    }

    if (cycleTimer) {
      window.clearInterval(cycleTimer);
      cycleTimer = null;
    }

    window.removeEventListener("resize", handleWindowResize);

    if (resizeObserver && vizContainer) {
      try {
        resizeObserver.unobserve(vizContainer);
      } catch {
        // ignore
      }
      resizeObserver = null;
    }

    // We intentionally do NOT close the AudioContext,
    // so audio playback stays alive if the visualizer is torn down.
  }

  //   function getPresetName() {
  //   // If we haven't successfully loaded anything yet, fall back to a friendly label
  //   if (!currentPresetKey) return 'Default';
  //   return currentPresetKey;
  // }

  // Initialize preset label on setup (viz starts OFF)
  updatePresetLabel();

  return {
    setEnabled,
    isEnabled,
    destroy,
    audioContext: audioCtx,
    visualizer,
    //getPresetName, // 🔹 new
  };
}
